/**
 * Constants for pageview tracking.
 */

import type { RouteMapping } from './types.js';

/**
 * Route mappings for pageview tracking.
 * Maps React Router paths to PostHog pageview paths.
 */
export const ROUTE_MAPPINGS = {
  // Auth routes
  '/login': {
    path: '/login',
    category: 'Log In'
  },
  '/register': {
    path: '/signup',
    category: 'Sign Up'
  },

  // Agent setup routes
  '/agent/setup': {
    path: '/agent/setup/select',
    category: 'Download',
    section: 'Select OS'
  },
  // ENG-4175: post-auth first-login segmentation wizard (before the download step).
  '/agent/setup/prequal': {
    path: '/agent/setup/prequal',
    category: 'Sign Up',
    section: 'Prequalification'
  },
  '/agent/setup/select': {
    path: '/agent/setup/select',
    category: 'Download',
    section: 'Select OS'
  },
  '/agent/setup/download/:os/:key': {
    path: '/agent/setup/download',
    category: 'Download',
    section: 'Download Installer'
  },
  '/agent/setup/link': {
    path: '/agent/setup/link',
    category: 'Download',
    section: 'Link Bridge'
  },

  // Room routes
  '/room/:roomId': {
    path: '/room',
    category: 'Studio'
  },
  '/room/:roomId/boot': {
    path: '/room/boot',
    category: 'Studio'
  },
  '/room/:roomId/inspect': {
    path: '/room/inspect',
    category: 'Studio'
  },
  '/room/:roomId/billing/:mode/:status?': {
    path: '/studio/billing',
    category: 'Studio Settings',
    section: 'Studio Billing'
  }
} as const satisfies Record<string, RouteMapping>;

/**
 * Settings panel mappings for virtual pageviews.
 */
export const SETTINGS_PANEL_MAPPINGS = {
  // Account settings
  'account-profile': {
    path: '/account/settings',
    category: 'Account Settings',
    section: 'Account Settings'
  },
  'account-password': {
    path: '/account/settings/security',
    category: 'Account Settings',
    section: 'Account Security'
  },
  'account-hotkeys': {
    path: '/account/settings/hotkeys',
    category: 'Account Settings',
    section: 'Hotkeys'
  },
  'account-controllers': {
    path: '/account/settings/controllers',
    category: 'Account Settings',
    section: 'Controllers'
  },
  'account-gamepad': {
    path: '/account/settings/controllers/gamepad',
    category: 'Account Settings',
    section: 'Gamepad'
  },
  'account-gamepad-configure': {
    path: '/account/settings/controllers/gamepad/configure',
    category: 'Account Settings',
    section: 'Gamepad'
  },
  'account-ip-controller': {
    path: '/account/settings/controllers/ip-controller',
    category: 'Account Settings',
    section: 'IP Controller'
  },
  'account-ip-controller-configure': {
    path: '/account/settings/controllers/ip-controller/configure',
    category: 'Account Settings',
    section: 'IP Controller'
  },
  'account-ip-controller-test': {
    path: '/account/settings/controllers/ip-controller/test',
    category: 'Account Settings',
    section: 'IP Controller'
  },

  // Studio settings
  'studio-settings': {
    path: '/studio/settings',
    category: 'Studio Settings',
    section: 'Studio Settings'
  },
  'studio-billing': {
    path: '/studio/billing',
    category: 'Studio Settings',
    section: 'Studio Billing'
  },
  'studio-billing-select-plan': {
    path: '/studio/billing/select-plan',
    category: 'Studio Settings',
    section: 'Studio Billing'
  },
  'studio-billing-update-card': {
    path: '/studio/billing/update-card',
    category: 'Studio Settings',
    section: 'Studio Billing'
  },

  // Organization settings
  'organization-settings': {
    path: '/organization/settings',
    category: 'Organization Settings',
    section: 'Organization Settings'
  },

  // Advanced settings
  'advanced-settings': {
    path: '/settings/advanced',
    category: 'Advanced Settings',
    section: 'Advanced Settings'
  },
  'advanced-settings-video': {
    path: '/advanced-settings/video',
    category: 'Advanced Settings',
    section: 'Video'
  },
  'advanced-settings-control': {
    path: '/advanced-settings/control',
    category: 'Advanced Settings',
    section: 'Control'
  },
  'advanced-settings-audio': {
    path: '/advanced-settings/audio',
    category: 'Advanced Settings',
    section: 'Audio'
  },
  'advanced-settings-network': {
    path: '/advanced-settings/network',
    category: 'Advanced Settings',
    section: 'Network'
  },
  'advanced-settings-features': {
    path: '/advanced-settings/features',
    category: 'Advanced Settings',
    section: 'Features'
  },
  'advanced-settings-system': {
    path: '/advanced-settings/system',
    category: 'Advanced Settings',
    section: 'System'
  },
  'advanced-settings-iris-connection': {
    path: '/advanced-settings/iris-connection',
    category: 'Advanced Settings',
    section: 'Iris Connection'
  },
  'iris-connection-monitoring': {
    path: '/advanced-settings/iris-connection/monitoring',
    category: 'Iris Connection',
    section: 'Monitoring'
  },
  'iris-connection-video': {
    path: '/advanced-settings/iris-connection/video',
    category: 'Iris Connection',
    section: 'Video'
  },
  'iris-connection-control': {
    path: '/advanced-settings/iris-connection/control',
    category: 'Iris Connection',
    section: 'Control'
  },
  'iris-connection-driver': {
    path: '/advanced-settings/iris-connection/driver',
    category: 'Iris Connection',
    section: 'Driver'
  }
} as const satisfies Record<string, RouteMapping>;

/**
 * Workflow step mappings for virtual pageviews.
 */
export const WORKFLOW_MAPPINGS = {
  // Share/Invite workflow
  'share-invite': {
    path: '/share',
    category: 'Share',
    section: 'Share (Invite)'
  },

  // Add Video Source workflow
  'video-source-discover': {
    path: '/video-source/discover',
    category: 'Add Video Source',
    section: 'Discover Video Sources',
    requiresFlowId: true
  },
  'video-source-credentials': {
    path: '/video-source/credentials',
    category: 'Add Video Source',
    section: 'Device Credentials',
    requiresFlowId: true
  },
  'video-source-configure-rtsp': {
    path: '/video-source/configure/rtsp',
    category: 'Add Video Source',
    section: 'Configure Video – RTSP/ONVIF',
    requiresFlowId: true
  },
  'video-source-configure-ndi': {
    path: '/video-source/configure/ndi',
    category: 'Add Video Source',
    section: 'Configure Video – NDI',
    requiresFlowId: true
  },
  'video-source-configure-usb': {
    path: '/video-source/configure/usb',
    category: 'Add Video Source',
    section: 'Configure Video – USB',
    requiresFlowId: true
  },
  'video-source-configure-manual': {
    path: '/video-source/configure/manual',
    category: 'Add Video Source',
    section: 'Manual RTSP',
    requiresFlowId: true
  },
  'video-source-select-driver': {
    path: '/video-source/select-driver',
    category: 'Add Video Source',
    section: 'Select Driver',
    requiresFlowId: true
  },
  'video-source-connect-controls': {
    path: '/video-source/connect-controls',
    category: 'Add Video Source',
    section: 'Connect Controls',
    requiresFlowId: true
  },

  // Add Audio Source workflow
  'audio-source-discover': {
    path: '/audio-source/discover',
    category: 'Add Audio Source',
    section: 'Discover Audio Sources',
    requiresFlowId: true
  },
  'audio-source-configure': {
    path: '/audio-source/configure',
    category: 'Add Audio Source',
    section: 'Configure Audio',
    requiresFlowId: true
  },

  // Recording
  'recording-settings': {
    path: '/recording',
    category: 'Recording',
    section: 'Recording Settings'
  },

  // Output
  'output-settings': {
    path: '/output',
    category: 'Output',
    section: 'Output Settings (root)'
  },
  'output-ndi': {
    path: '/output/ndi',
    category: 'Output',
    section: 'Output – NDI'
  },
  'output-rtmp': {
    path: '/output/rtmp',
    category: 'Output',
    section: 'Output – RTMP'
  }
} as const satisfies Record<string, RouteMapping>;
